import {FC, Fragment} from 'react'
import {WorkflowStateModel} from '../../core/_models'
import {toTitle} from '../../helpers/generals'

// Every workflow config, whatever its status names, moves through these three
// kinds — the same ranking the backend uses for an action's `direction`.
const KINDS = [
  {kind: 'draft', label: 'Draft'},
  {kind: 'review', label: 'Review'},
  {kind: 'final', label: 'Final'},
] as const

/**
 * Where the schedule is in its approval process: Draft → Review → Final, the
 * current step named by the actual status ("Awaiting DM Approval"), done
 * steps ticked. Kinds rather than statuses, so a company's own workflow (a
 * two-level review, renamed states) still reads as the same three steps.
 *
 * Before the workflow has loaded, or for a status the config does not define,
 * it falls back to the plain status name.
 */
const StatusPath: FC<{status: WorkflowStateModel | null; fallbackKey?: string}> = ({
  status,
  fallbackKey,
}) => {
  const current = KINDS.findIndex((k) => k.kind === status?.kind)
  if (current < 0) {
    const text = status?.label || toTitle(status?.key || fallbackKey || '')
    return text ? (
      <span className='badge bg-gray-200 text-gray-700 fs-8 fw-bold'>{text}</span>
    ) : null
  }

  return (
    <ol className='d-flex align-items-center list-unstyled m-0 gap-2' aria-label='Schedule status'>
      {KINDS.map((k, i) => {
        const done = i < current
        const isCurrent = i === current
        return (
          <Fragment key={k.kind}>
            {i > 0 && (
              <li aria-hidden='true'>
                <span
                  className={`d-block ${done || isCurrent ? 'bg-primary' : 'bg-gray-300'}`}
                  style={{width: 20, height: 2}}
                />
              </li>
            )}
            <li
              aria-current={isCurrent ? 'step' : undefined}
              className={`d-flex align-items-center gap-1 text-nowrap fs-7 ${
                isCurrent
                  ? 'badge badge-light-primary fw-bold px-3 py-2'
                  : done
                  ? 'text-primary fw-semibold'
                  : 'text-gray-500'
              }`}
            >
              {done && <i className='bi bi-check-circle-fill text-primary fs-7'></i>}
              {isCurrent && <i className='bi bi-record-circle text-primary fs-7'></i>}
              {!done && !isCurrent && <i className='bi bi-circle text-gray-400 fs-7'></i>}
              {isCurrent ? status?.label || toTitle(status?.key || '') : k.label}
            </li>
          </Fragment>
        )
      })}
    </ol>
  )
}

export {StatusPath}
